/**
 * Toast notifications (§18). A minimal provider + `useToast()` hook + a fixed
 * Toaster stack. Tone reuses the status palette (success/warning/danger/info).
 * Auto-dismisses; respects reduced-motion via CSS. Mounted once at the app root.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Icon, type IconName } from "./Icon.js";

export type ToastTone = "info" | "success" | "warning" | "danger";

interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
}

interface ToastApi {
  toast: (message: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONE_ICON: Record<ToastTone, IconName> = {
  info: "dot",
  success: "check",
  warning: "alert",
  danger: "alert",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const toast = useCallback((message: string, tone: ToastTone = "info") => {
    const id = ++seq.current;
    setItems((cur) => [...cur, { id, message, tone }]);
    setTimeout(() => setItems((cur) => cur.filter((t) => t.id !== id)), 3500);
  }, []);

  const api = useMemo<ToastApi>(() => ({ toast }), [toast]);

  // Errors interrupt (assertive); everything else is polite so it doesn't
  // talk over the operator.
  const polite = items.filter((t) => t.tone !== "danger");
  const assertive = items.filter((t) => t.tone === "danger");
  const render = (t: ToastItem) => (
    <div key={t.id} className={`toast toast--${t.tone}`}>
      <Icon name={TONE_ICON[t.tone]} />
      <span>{t.message}</span>
    </div>
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toaster">
        <div className="toaster__region" role="status" aria-live="polite">
          {polite.map(render)}
        </div>
        <div className="toaster__region" role="alert" aria-live="assertive">
          {assertive.map(render)}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

/** Returns the toast fn. Safe no-op if used outside a provider (e.g. preview). */
export function useToast(): ToastApi {
  return useContext(ToastContext) ?? { toast: () => undefined };
}
